/**
 * ColumnHeaderContextMenu Component
 *
 * Right-click context menu for column headers.
 * Provides options to remove columns and set column statistics.
 */

import { FC, useEffect, useRef } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { 
  EyeInvisibleOutlined, 
  CalculatorOutlined,
  CheckOutlined,
  InsertRowLeftOutlined,
} from '@ant-design/icons';
import type { ColumnStatistic } from '#types';

interface ColumnHeaderContextMenuProps {
  columnId: string;
  columnHeader: string;
  valueType: 'string' | 'number' | 'date' | 'array';
  position: { x: number; y: number };
  isRequired: boolean;
  currentStatistic: ColumnStatistic | null;
  onClose: () => void;
  onRemoveColumn: (columnId: string) => void;
  onSetStatistic: (columnId: string, stat: ColumnStatistic | null) => void;
  onRequestInsertColumn: (anchorColumnId: string, position: { x: number; y: number }) => void;
}

const ColumnHeaderContextMenu: FC<ColumnHeaderContextMenuProps> = ({
  columnId,
  valueType,
  position,
  isRequired,
  currentStatistic,
  onClose,
  onRemoveColumn,
  onSetStatistic,
  onRequestInsertColumn,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);

  // Focus the trigger element to enable keyboard navigation
  useEffect(() => {
    triggerRef.current?.focus();
  }, []);

  const statisticItems: MenuProps['items'] = [
    {
      key: 'stat-none',
      label: 'None',
      icon: currentStatistic === null ? <CheckOutlined /> : <span style={{ width: 14 }} />,
      onClick: () => {
        onSetStatistic(columnId, null);
        onClose();
      },
    },
    {
      key: 'stat-count',
      label: 'Count',
      icon: currentStatistic === 'count' ? <CheckOutlined /> : <span style={{ width: 14 }} />,
      onClick: () => {
        onSetStatistic(columnId, 'count');
        onClose();
      },
    },
  ];

  // Add Sum and Avg for numeric columns
  if (valueType === 'number') {
    statisticItems.push(
      {
        key: 'stat-sum',
        label: 'Sum',
        icon: currentStatistic === 'sum' ? <CheckOutlined /> : <span style={{ width: 14 }} />,
        onClick: () => {
          onSetStatistic(columnId, 'sum');
          onClose();
        },
      },
      {
        key: 'stat-avg',
        label: 'Average',
        icon: currentStatistic === 'avg' ? <CheckOutlined /> : <span style={{ width: 14 }} />,
        onClick: () => {
          onSetStatistic(columnId, 'avg');
          onClose();
        },
      }
    );
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'remove',
      label: 'Remove from view',
      icon: <EyeInvisibleOutlined />,
      disabled: isRequired,
      onClick: () => {
        onRemoveColumn(columnId);
        onClose();
      },
    },
    {
      key: 'insert',
      label: 'Insert column',
      icon: <InsertRowLeftOutlined />,
      onClick: () => {
        onRequestInsertColumn(columnId, position);
        onClose();
      },
    },
    { type: 'divider' },
    {
      key: 'statistic',
      label: 'Column statistic',
      icon: <CalculatorOutlined />,
      children: statisticItems,
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      open={true}
      onOpenChange={open => !open && onClose()}
      trigger={['contextMenu']}
    >
      <div
        ref={triggerRef}
        tabIndex={-1}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
    </Dropdown>
  );
};

export default ColumnHeaderContextMenu;

